/**
 * ProjectRelationshipsPage
 *
 * NOTE: This page is no longer linked in the sidebar navigation as of the relationship UX redesign.
 *
 * Why this page still exists:
 * - Relationship linking is now done via inline dialogs on clip/VFX detail pages (much cleaner UX)
 * - This page is kept for backwards compatibility (direct URLs, bookmarks)
 * - Displays an info banner directing users to the new workflow
 * - Could be useful in the future for bulk relationship management or visualization features
 *
 * New workflow location: LinkRelationshipDialog component used in:
 * - ClipDetailContent (relationships section)
 * - VfxShotDetailContent (relationships section)
 * - ProjectClipsTab (link button on clips table)
 */
import { useState, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { ProjectLayout } from '@/components/layout/ProjectLayout'
import { useProject } from '@/hooks/useProjects'
import { useClips } from '@/hooks/useClips'
import { useVfxShots } from '@/hooks/useVfxShots'
import { useTags } from '@/hooks/useTags'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { TagBadge } from '@/components/tags/TagBadge'
import { Link2, Film, Wand2, Search, Filter, X } from 'lucide-react'
import { toast } from 'sonner'
import type { Clip, VfxShot, VfxShotStatus } from '@/types/database'
import { createClipRelationship } from '@/lib/api/clipRelationships'
import { updateVfxShot } from '@/lib/api/vfxShots'

type EntityType = 'clip' | 'vfx_shot'

interface LinkableItem {
  id: string
  type: EntityType
  name: string
  subtitle?: string
  tags?: string[]
  status?: VfxShotStatus
  linkCount: number
  data: Clip | VfxShot
}

export function ProjectRelationshipsPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: project, isLoading: projectLoading } = useProject(id)
  const { data: clips, isLoading: clipsLoading } = useClips(id)
  const { data: vfxData, isLoading: vfxLoading } = useVfxShots(id || '', { limit: 200 })
  const { data: tags } = useTags(id)

  // UI State
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedEntityTypes, setSelectedEntityTypes] = useState<Set<EntityType>>(new Set(['clip', 'vfx_shot']))
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [selectedVfxStatus, setSelectedVfxStatus] = useState<VfxShotStatus | 'all'>('all')
  const [isLinking, setIsLinking] = useState(false)

  // Visual selection mode - click an item to select it, then click another to link
  const [selectedItem, setSelectedItem] = useState<LinkableItem | null>(null)

  // Extract linking mode from URL params
  const linkFromParam = searchParams.get('link_from') // Format: "clip:abc-123" or "vfx_shot:def-456"
  const linkingMode = !!linkFromParam
  const [linkFromType, linkFromId] = linkFromParam?.split(':') || []

  const vfxShots = vfxData?.shots || []

  const isLoading = projectLoading || clipsLoading || vfxLoading

  // Build unified linkable items list
  const linkableItems: LinkableItem[] = useMemo(() => {
    const items: LinkableItem[] = []

    // Add clips
    if (clips) {
      clips.forEach(clip => {
        // TODO: Calculate actual link count from relationships API
        items.push({
          id: clip.id,
          type: 'clip',
          name: clip.name,
          subtitle: clip.tape_name ? `Tape: ${clip.tape_name} | TC: ${clip.source_timecode || 'N/A'}` : `TC: ${clip.source_timecode || 'N/A'}`,
          tags: clip.tags,
          linkCount: 0, // Placeholder
          data: clip
        })
      })
    }

    // Add VFX shots
    if (vfxShots) {
      vfxShots.forEach(shot => {
        items.push({
          id: shot.id,
          type: 'vfx_shot',
          name: shot.shot_name,
          subtitle: `Frames: ${shot.frame_range_start}-${shot.frame_range_end}`,
          tags: shot.tags,
          status: shot.status,
          linkCount: 0, // Placeholder
          data: shot
        })
      })
    }

    return items
  }, [clips, vfxShots])

  // Filter items based on search, entity types, tags, and VFX status
  const filteredItems = useMemo(() => {
    let filtered = linkableItems

    // Exclude the source item in linking mode
    if (linkingMode && linkFromId) {
      filtered = filtered.filter(item => !(item.type === linkFromType && item.id === linkFromId))
    }

    // Filter by entity type
    filtered = filtered.filter(item => selectedEntityTypes.has(item.type))

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(item => {
        const nameMatch = item.name.toLowerCase().includes(query)
        const subtitleMatch = item.subtitle?.toLowerCase().includes(query)
        const tagsMatch = item.tags?.some(tag => tag.toLowerCase().includes(query))
        return nameMatch || subtitleMatch || tagsMatch
      })
    }

    // Filter by tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter(item =>
        selectedTags.some(tag => item.tags?.includes(tag))
      )
    }

    // Filter by VFX status
    if (selectedVfxStatus !== 'all') {
      filtered = filtered.filter(item =>
        item.type === 'vfx_shot' && item.status === selectedVfxStatus
      )
    }

    return filtered
  }, [linkableItems, selectedEntityTypes, searchQuery, selectedTags, selectedVfxStatus, linkingMode, linkFromId, linkFromType])

  // Group items by entity type
  const groupedItems = useMemo(() => {
    const groups: Record<EntityType, LinkableItem[]> = {
      clip: [],
      vfx_shot: []
    }

    filteredItems.forEach(item => {
      groups[item.type].push(item)
    })

    return groups
  }, [filteredItems])

  // Toggle entity type filter
  const toggleEntityType = (type: EntityType) => {
    setSelectedEntityTypes(prev => {
      const newSet = new Set(prev)
      if (newSet.has(type)) {
        newSet.delete(type)
      } else {
        newSet.add(type)
      }
      return newSet
    })
  }

  // Handle linking
  const handleLinkTo = async (targetItem: LinkableItem) => {
    if (!linkFromType || !linkFromId) {
      toast.error('Invalid linking source')
      return
    }

    setIsLinking(true)

    try {
      // Determine link type and call appropriate API
      if (linkFromType === 'clip' && targetItem.type === 'clip') {
        // Clip to Clip relationship
        await createClipRelationship(linkFromId, {
          target_clip_id: targetItem.id,
          relationship_type: 'related'
        })
        toast.success(`Linked clips successfully`)
      } else if (linkFromType === 'clip' && targetItem.type === 'vfx_shot') {
        // Clip to VFX Shot (update VFX shot's clip_id)
        await updateVfxShot(targetItem.id, {
          clip_id: linkFromId
        })
        toast.success(`Linked clip to VFX shot: ${targetItem.name}`)
      } else if (linkFromType === 'vfx_shot' && targetItem.type === 'clip') {
        // VFX Shot to Clip (update VFX shot's clip_id)
        await updateVfxShot(linkFromId, {
          clip_id: targetItem.id
        })
        toast.success(`Linked VFX shot to clip: ${targetItem.name}`)
      } else {
        // VFX Shot to VFX Shot or other combinations
        toast.info('This link type is not yet supported')
      }
    } catch (error: any) {
      console.error('Linking error:', error)
      toast.error(error.message || 'Failed to create link')
    } finally {
      setIsLinking(false)
    }
  }

  // Exit linking mode
  const exitLinkingMode = () => {
    setSearchParams({})
  }

  // Get source item name for linking mode
  const getSourceItemName = () => {
    if (!linkFromId) return ''
    const sourceItem = linkableItems.find(item => item.id === linkFromId && item.type === linkFromType)
    return sourceItem?.name || 'Unknown'
  }

  // Handle item card click - select or deselect
  const handleItemClick = (item: LinkableItem) => {
    if (selectedItem?.id === item.id) {
      // Clicking selected item again deselects it
      setSelectedItem(null)
    } else {
      // Select this item
      setSelectedItem(item)
    }
  }

  // Handle linking selected item to target item
  const handleLinkSelectedTo = async (targetItem: LinkableItem) => {
    if (!selectedItem) {
      toast.error('No item selected')
      return
    }

    if (selectedItem.id === targetItem.id) {
      toast.error('Cannot link an item to itself')
      return
    }

    setIsLinking(true)

    try {
      // Determine link type and call appropriate API
      if (selectedItem.type === 'clip' && targetItem.type === 'clip') {
        await createClipRelationship(selectedItem.id, {
          target_clip_id: targetItem.id,
          relationship_type: 'related'
        })
        toast.success(`Linked: ${selectedItem.name} → ${targetItem.name}`)
      } else if (selectedItem.type === 'clip' && targetItem.type === 'vfx_shot') {
        await updateVfxShot(targetItem.id, {
          clip_id: selectedItem.id
        })
        toast.success(`Linked clip to VFX shot: ${targetItem.name}`)
      } else if (selectedItem.type === 'vfx_shot' && targetItem.type === 'clip') {
        await updateVfxShot(selectedItem.id, {
          clip_id: targetItem.id
        })
        toast.success(`Linked VFX shot to clip: ${targetItem.name}`)
      } else {
        toast.info('This link type is not yet supported')
      }

      // Clear selection after successful link
      setSelectedItem(null)
    } catch (error: any) {
      console.error('Link creation error:', error)
      toast.error(error.message || 'Failed to create link')
    } finally {
      setIsLinking(false)
    }
  }

  const getStatusBadge = (status: VfxShotStatus) => {
    const statusConfig: Record<VfxShotStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      yellow: { label: 'Pending', variant: 'outline' },
      green: { label: 'Approved', variant: 'default' },
      in_progress: { label: 'In Progress', variant: 'secondary' },
      review_ready: { label: 'Review', variant: 'secondary' },
      approved: { label: 'Final', variant: 'default' },
      omit: { label: 'Omitted', variant: 'destructive' },
      back_in: { label: 'Back In', variant: 'outline' }
    }

    const config = statusConfig[status]
    return <Badge variant={config.variant}>{config.label}</Badge>
  }

  if (isLoading) {
    return (
      <ProjectLayout projectName={project?.name}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading relationships...</p>
          </div>
        </div>
      </ProjectLayout>
    )
  }

  return (
    <ProjectLayout projectName={project?.name}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Link2 className="h-8 w-8" />
            Relationships
          </h1>
          <p className="text-muted-foreground mt-2">
            {linkingMode
              ? `Select items to link to: ${getSourceItemName()}`
              : selectedItem
              ? `Click another item to link with: ${selectedItem.name}`
              : 'Click any item to select it, then click another to create a link'
            }
          </p>
        </div>

        {/* Linking Mode Banner */}
        {linkingMode && (
          <Card className="border-primary bg-primary/5">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Link2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Linking Mode Active</h3>
                    <p className="text-sm text-muted-foreground">
                      Link FROM: <span className="font-medium text-foreground">{getSourceItemName()}</span>
                    </p>
                  </div>
                </div>
                <Button variant="outline" onClick={exitLinkingMode}>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Visual Selection Banner */}
        {!linkingMode && selectedItem && (
          <Card className="border-primary bg-primary/5">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    {selectedItem.type === 'clip' ? (
                      <Film className="h-5 w-5 text-primary" />
                    ) : (
                      <Wand2 className="h-5 w-5 text-primary" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold">Item Selected</h3>
                    <p className="text-sm text-muted-foreground">
                      Selected: <span className="font-medium text-foreground">{selectedItem.name}</span>
                      <span className="mx-2">•</span>
                      <span className="text-xs">Click another item to link, or click selected item again to deselect</span>
                    </p>
                  </div>
                </div>
                <Button variant="outline" onClick={() => setSelectedItem(null)}>
                  <X className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search and Filters */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, tags, or metadata..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4">
              {/* Entity Type Toggles */}
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Show:</span>
                <Button
                  variant={selectedEntityTypes.has('clip') ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleEntityType('clip')}
                >
                  <Film className="h-4 w-4 mr-2" />
                  Clips ({groupedItems.clip.length})
                </Button>
                <Button
                  variant={selectedEntityTypes.has('vfx_shot') ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleEntityType('vfx_shot')}
                >
                  <Wand2 className="h-4 w-4 mr-2" />
                  VFX Shots ({groupedItems.vfx_shot.length})
                </Button>
              </div>

              {/* Tag Filter */}
              {tags && tags.length > 0 && (
                <Select
                  value={selectedTags[0] || 'all'}
                  onValueChange={(value) => setSelectedTags(value === 'all' ? [] : [value])}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by tag" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tags</SelectItem>
                    {tags.map(tag => (
                      <SelectItem key={tag.id} value={tag.name}>
                        {tag.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* VFX Status Filter */}
              {selectedEntityTypes.has('vfx_shot') && (
                <Select
                  value={selectedVfxStatus}
                  onValueChange={(value) => setSelectedVfxStatus(value as VfxShotStatus | 'all')}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="yellow">Pending</SelectItem>
                    <SelectItem value="green">Approved</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="review_ready">Review</SelectItem>
                    <SelectItem value="approved">Final</SelectItem>
                    <SelectItem value="omit">Omitted</SelectItem>
                    <SelectItem value="back_in">Back In</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <div className="space-y-6">
          {/* Clips Section */}
          {selectedEntityTypes.has('clip') && groupedItems.clip.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Film className="h-5 w-5" />
                Clips ({groupedItems.clip.length})
              </h2>
              <div className="grid gap-3">
                {groupedItems.clip.map(item => {
                  const isSelected = selectedItem?.id === item.id
                  const canLinkToThis = !linkingMode && selectedItem && selectedItem.id !== item.id

                  return (
                    <Card
                      key={item.id}
                      className={`transition-all cursor-pointer ${
                        isSelected
                          ? 'ring-2 ring-primary bg-primary/5 hover:bg-primary/10'
                          : canLinkToThis
                          ? 'hover:bg-accent/50 hover:ring-1 hover:ring-primary/50'
                          : 'hover:bg-accent/50'
                      }`}
                      onClick={() => !linkingMode && handleItemClick(item)}
                    >
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium">{item.name}</h3>
                              {isSelected && (
                                <Badge variant="default" className="text-xs">SELECTED</Badge>
                              )}
                            </div>
                            {item.subtitle && (
                              <p className="text-sm text-muted-foreground mb-2">{item.subtitle}</p>
                            )}
                            {item.tags && item.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {item.tags.slice(0, 5).map(tag => {
                                  const tagData = tags?.find(t => t.name === tag)
                                  return tagData ? (
                                    <TagBadge key={tag} tag={tagData} />
                                  ) : (
                                    <Badge key={tag} variant="secondary">{tag}</Badge>
                                  )
                                })}
                                {item.tags.length > 5 && (
                                  <Badge variant="outline">+{item.tags.length - 5} more</Badge>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            {!linkingMode && !selectedItem && (
                              <Badge variant="secondary" className="shrink-0">
                                <Link2 className="h-3 w-3 mr-1" />
                                {item.linkCount}
                              </Badge>
                            )}
                            {linkingMode && (
                              <Button
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleLinkTo(item)
                                }}
                                disabled={isLinking}
                              >
                                Link →
                              </Button>
                            )}
                            {!linkingMode && canLinkToThis && (
                              <Button
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleLinkSelectedTo(item)
                                }}
                                disabled={isLinking}
                              >
                                Link to {selectedItem.name.slice(0, 15)}
                                {selectedItem.name.length > 15 && '...'}
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </div>
          )}

          {/* VFX Shots Section */}
          {selectedEntityTypes.has('vfx_shot') && groupedItems.vfx_shot.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Wand2 className="h-5 w-5" />
                VFX Shots ({groupedItems.vfx_shot.length})
              </h2>
              <div className="grid gap-3">
                {groupedItems.vfx_shot.map(item => {
                  const isSelected = selectedItem?.id === item.id
                  const canLinkToThis = !linkingMode && selectedItem && selectedItem.id !== item.id

                  return (
                    <Card
                      key={item.id}
                      className={`transition-all cursor-pointer ${
                        isSelected
                          ? 'ring-2 ring-primary bg-primary/5 hover:bg-primary/10'
                          : canLinkToThis
                          ? 'hover:bg-accent/50 hover:ring-1 hover:ring-primary/50'
                          : 'hover:bg-accent/50'
                      }`}
                      onClick={() => !linkingMode && handleItemClick(item)}
                    >
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium">{item.name}</h3>
                              {isSelected && (
                                <Badge variant="default" className="text-xs">SELECTED</Badge>
                              )}
                              {item.status && getStatusBadge(item.status)}
                            </div>
                            {item.subtitle && (
                              <p className="text-sm text-muted-foreground mb-2">{item.subtitle}</p>
                            )}
                            {item.tags && item.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {item.tags.slice(0, 5).map(tag => {
                                  const tagData = tags?.find(t => t.name === tag)
                                  return tagData ? (
                                    <TagBadge key={tag} tag={tagData} />
                                  ) : (
                                    <Badge key={tag} variant="secondary">{tag}</Badge>
                                  )
                                })}
                                {item.tags.length > 5 && (
                                  <Badge variant="outline">+{item.tags.length - 5} more</Badge>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            {!linkingMode && !selectedItem && (
                              <Badge variant="secondary" className="shrink-0">
                                <Link2 className="h-3 w-3 mr-1" />
                                {item.linkCount}
                              </Badge>
                            )}
                            {linkingMode && (
                              <Button
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleLinkTo(item)
                                }}
                                disabled={isLinking}
                              >
                                Link →
                              </Button>
                            )}
                            {!linkingMode && canLinkToThis && (
                              <Button
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleLinkSelectedTo(item)
                                }}
                                disabled={isLinking}
                              >
                                Link to {selectedItem.name.slice(0, 15)}
                                {selectedItem.name.length > 15 && '...'}
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </div>
          )}

          {/* No Results */}
          {filteredItems.length === 0 && (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12">
                  <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No items found</h3>
                  <p className="text-sm text-muted-foreground">
                    Try adjusting your filters or search query
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </ProjectLayout>
  )
}
